/* tslint:disable */
/* eslint-disable */
/**
 * Scrivio Client Portal Authentication API
 * Authentication API for the Scrivio Client Portal.  ## Overview These endpoints support the passwordless Email Link authentication flow for clients. Clients are end-users (not healthcare workers) who access the client portal at mijn.scrivio.nl.  ## Authentication Flow 1. Client enters email on portal login page 2. Frontend calls `/api/client-auth/check-email` 3. The **backend** decides whether the address names a client record, and sends    either the Firebase Email Link or a note saying no record is registered on    that address. The response is the same either way. 4. Client clicks magic link in email 5. Frontend calls `/api/client-auth/complete-signin` after Firebase auth completes 6. Backend sets custom claims and links Firebase user to Client entity  Step 3 used to be the frontend\'s: it read the uniform 200 as \"go ahead\" and sent a Firebase link to every address typed into the form, including addresses no client record named — which created an orphaned Firebase account the moment the link was clicked (#3863). A decision the client may not know cannot be a decision the client makes.  ## Error Responses All error responses follow RFC 7807 Problem Details format. 
 *
 * The version of the OpenAPI document: 1.0.0
 * Contact: support@scrivio.nl
 *
 * NOTE: This class is auto generated by OpenAPI Generator (https://openapi-generator.tech).
 * https://openapi-generator.tech
 * Do not edit the class manually.
 */


import type { Configuration } from './configuration';
import type { AxiosPromise, AxiosInstance, RawAxiosRequestConfig } from 'axios';
import globalAxios from 'axios';
// Some imports not used depending on template conditions
// @ts-ignore
import { DUMMY_BASE_URL, assertParamExists, setApiKeyToObject, setBasicAuthToObject, setBearerAuthToObject, setOAuthToObject, setSearchParams, serializeDataIfNeeded, toPathString, createRequestFunction } from './common';
import type { RequestArgs } from './base';
// @ts-ignore
import { BASE_PATH, COLLECTION_FORMATS, BaseAPI, RequiredError, operationServerMap } from './base';

export interface CheckEmailRequest {
    /**
     * The address to send the sign-in link to
     */
    'email': string;
}
export interface CheckEmailResponse {
    /**
     * Always true. It means \"the request was accepted\", never \"the address is known\" — see the endpoint description. 
     */
    'success': boolean;
}
export interface ClientAuthResponse {
    /**
     * The ID of the Client entity
     */
    'clientId': string;
    /**
     * The organization the client belongs to
     */
    'organizationId': string;
    /**
     * If true, the frontend must refresh the Firebase token to get the updated custom claims. This is always true after first sign-in. 
     */
    'requiresTokenRefresh': boolean;
}
/**
 * RFC 7807 Problem Details for HTTP APIs
 */
export interface ProblemDetail {
    /**
     * URI reference identifying the problem type
     */
    'type': string;
    /**
     * Short, human-readable summary
     */
    'title': string;
    /**
     * HTTP status code
     */
    'status': number;
    /**
     * Human-readable explanation
     */
    'detail'?: string;
    /**
     * URI reference identifying the specific occurrence
     */
    'instance'?: string;
    /**
     * List of validation errors
     */
    'errors'?: Array<ProblemDetailErrorsInner>;
}
export interface ProblemDetailErrorsInner {
    /**
     * Field name that failed validation
     */
    'field'?: string;
    /**
     * Error message
     */
    'message'?: string;
}

/**
 * ClientAuthApi - axios parameter creator
 */
export const ClientAuthApiAxiosParamCreator = function (configuration?: Configuration) {
    return {
        /**
         * Sends the client portal\'s magic sign-in link to the supplied address when a client record names it, and an informational email saying no record is registered on that address when none does. Exactly one email goes out either way, and the frontend sends nothing itself.  **Security note (ADR-013):** to prevent email enumeration this endpoint answers 200 OK with the same body in both cases. The difference reaches the mailbox only — and even there the two mails share a subject line. A Firebase sign-in link is minted *only* for an address a client record names, because redeeming one creates a Firebase Auth account, and an account with no `clients` row behind it is what locks a client out later (#2762/#2763/#2764).  The path and operation are still named `check-email`, which is what they did before the send moved server-side. Renaming the path would 404 every browser still running the previous frontend bundle for the whole of a rolling deploy, on the only route a client has into their dossier, so the name is historical and this description is what carries the meaning.  Rate limited per requested address and per source IP. The 429 is enumeration-safe: an unknown address is throttled exactly like a known one. 
         * @summary Request a client portal sign-in link
         * @param {CheckEmailRequest} checkEmailRequest 
         * @param {*} [options] Override http request option.
         * @throws {RequiredError}
         */
        checkClientEmail: async (checkEmailRequest: CheckEmailRequest, options: RawAxiosRequestConfig = {}): Promise<RequestArgs> => {
            // verify required parameter 'checkEmailRequest' is not null or undefined
            assertParamExists('checkClientEmail', 'checkEmailRequest', checkEmailRequest)
            const localVarPath = `/api/client-auth/check-email`;
            // use dummy base URL string because the URL constructor only accepts absolute URLs.
            const localVarUrlObj = new URL(localVarPath, DUMMY_BASE_URL);
            let baseOptions;
            if (configuration) {
                baseOptions = configuration.baseOptions;
            }

            const localVarRequestOptions = { method: 'POST', ...baseOptions, ...options};
            const localVarHeaderParameter = {} as any;
            const localVarQueryParameter = {} as any;

            localVarHeaderParameter['Content-Type'] = 'application/json';
            localVarHeaderParameter['Accept'] = 'application/json';

            setSearchParams(localVarUrlObj, localVarQueryParameter);
            let headersFromBaseOptions = baseOptions && baseOptions.headers ? baseOptions.headers : {};
            localVarRequestOptions.headers = {...localVarHeaderParameter, ...headersFromBaseOptions, ...options.headers};
            localVarRequestOptions.data = serializeDataIfNeeded(checkEmailRequest, localVarRequestOptions, configuration)

            return {
                url: toPathString(localVarUrlObj),
                options: localVarRequestOptions,
            };
        },
        /**
         * Called after Firebase Email Link sign-in completes successfully. This endpoint: 1. Validates the Firebase token 2. Finds the Client entity by email 3. Sets custom claims on the Firebase user (userType, organizationId, clientId) 4. Links the Firebase UID to the Client entity  The response includes the client and organization IDs needed by the portal.  **Important:** After calling this endpoint, the frontend must refresh the Firebase token to get the updated custom claims. 
         * @summary Complete client portal sign-in
         * @param {*} [options] Override http request option.
         * @throws {RequiredError}
         */
        completeClientSignIn: async (options: RawAxiosRequestConfig = {}): Promise<RequestArgs> => {
            const localVarPath = `/api/client-auth/complete-signin`;
            // use dummy base URL string because the URL constructor only accepts absolute URLs.
            const localVarUrlObj = new URL(localVarPath, DUMMY_BASE_URL);
            let baseOptions;
            if (configuration) {
                baseOptions = configuration.baseOptions;
            }

            const localVarRequestOptions = { method: 'POST', ...baseOptions, ...options};
            const localVarHeaderParameter = {} as any;
            const localVarQueryParameter = {} as any;

            // authentication firebaseAuth required
            // http bearer authentication required
            await setBearerAuthToObject(localVarHeaderParameter, configuration)

            localVarHeaderParameter['Accept'] = 'application/json';

            setSearchParams(localVarUrlObj, localVarQueryParameter);
            let headersFromBaseOptions = baseOptions && baseOptions.headers ? baseOptions.headers : {};
            localVarRequestOptions.headers = {...localVarHeaderParameter, ...headersFromBaseOptions, ...options.headers};

            return {
                url: toPathString(localVarUrlObj),
                options: localVarRequestOptions,
            };
        },
    }
};

/**
 * ClientAuthApi - functional programming interface
 */
export const ClientAuthApiFp = function(configuration?: Configuration) {
    const localVarAxiosParamCreator = ClientAuthApiAxiosParamCreator(configuration)
    return {
        /**
         * Sends the client portal\'s magic sign-in link to the supplied address when a client record names it, and an informational email saying no record is registered on that address when none does. Exactly one email goes out either way, and the frontend sends nothing itself.  **Security note (ADR-013):** to prevent email enumeration this endpoint answers 200 OK with the same body in both cases. The difference reaches the mailbox only — and even there the two mails share a subject line. A Firebase sign-in link is minted *only* for an address a client record names, because redeeming one creates a Firebase Auth account, and an account with no `clients` row behind it is what locks a client out later (#2762/#2763/#2764).  The path and operation are still named `check-email`, which is what they did before the send moved server-side. Renaming the path would 404 every browser still running the previous frontend bundle for the whole of a rolling deploy, on the only route a client has into their dossier, so the name is historical and this description is what carries the meaning.  Rate limited per requested address and per source IP. The 429 is enumeration-safe: an unknown address is throttled exactly like a known one. 
         * @summary Request a client portal sign-in link
         * @param {CheckEmailRequest} checkEmailRequest 
         * @param {*} [options] Override http request option.
         * @throws {RequiredError}
         */
        async checkClientEmail(checkEmailRequest: CheckEmailRequest, options?: RawAxiosRequestConfig): Promise<(axios?: AxiosInstance, basePath?: string) => AxiosPromise<CheckEmailResponse>> {
            const localVarAxiosArgs = await localVarAxiosParamCreator.checkClientEmail(checkEmailRequest, options);
            const localVarOperationServerIndex = configuration?.serverIndex ?? 0;
            const localVarOperationServerBasePath = operationServerMap['ClientAuthApi.checkClientEmail']?.[localVarOperationServerIndex]?.url;
            return (axios, basePath) => createRequestFunction(localVarAxiosArgs, globalAxios, BASE_PATH, configuration)(axios, localVarOperationServerBasePath || basePath);
        },
        /**
         * Called after Firebase Email Link sign-in completes successfully. This endpoint: 1. Validates the Firebase token 2. Finds the Client entity by email 3. Sets custom claims on the Firebase user (userType, organizationId, clientId) 4. Links the Firebase UID to the Client entity  The response includes the client and organization IDs needed by the portal.  **Important:** After calling this endpoint, the frontend must refresh the Firebase token to get the updated custom claims. 
         * @summary Complete client portal sign-in
         * @param {*} [options] Override http request option.
         * @throws {RequiredError}
         */
        async completeClientSignIn(options?: RawAxiosRequestConfig): Promise<(axios?: AxiosInstance, basePath?: string) => AxiosPromise<ClientAuthResponse>> {
            const localVarAxiosArgs = await localVarAxiosParamCreator.completeClientSignIn(options);
            const localVarOperationServerIndex = configuration?.serverIndex ?? 0;
            const localVarOperationServerBasePath = operationServerMap['ClientAuthApi.completeClientSignIn']?.[localVarOperationServerIndex]?.url;
            return (axios, basePath) => createRequestFunction(localVarAxiosArgs, globalAxios, BASE_PATH, configuration)(axios, localVarOperationServerBasePath || basePath);
        },
    }
};

/**
 * ClientAuthApi - factory interface
 */
export const ClientAuthApiFactory = function (configuration?: Configuration, basePath?: string, axios?: AxiosInstance) {
    const localVarFp = ClientAuthApiFp(configuration)
    return {
        /**
         * Sends the client portal\'s magic sign-in link to the supplied address when a client record names it, and an informational email saying no record is registered on that address when none does. Exactly one email goes out either way, and the frontend sends nothing itself.  **Security note (ADR-013):** to prevent email enumeration this endpoint answers 200 OK with the same body in both cases. The difference reaches the mailbox only — and even there the two mails share a subject line. A Firebase sign-in link is minted *only* for an address a client record names, because redeeming one creates a Firebase Auth account, and an account with no `clients` row behind it is what locks a client out later (#2762/#2763/#2764).  The path and operation are still named `check-email`, which is what they did before the send moved server-side. Renaming the path would 404 every browser still running the previous frontend bundle for the whole of a rolling deploy, on the only route a client has into their dossier, so the name is historical and this description is what carries the meaning.  Rate limited per requested address and per source IP. The 429 is enumeration-safe: an unknown address is throttled exactly like a known one. 
         * @summary Request a client portal sign-in link
         * @param {CheckEmailRequest} checkEmailRequest 
         * @param {*} [options] Override http request option.
         * @throws {RequiredError}
         */
        checkClientEmail(checkEmailRequest: CheckEmailRequest, options?: RawAxiosRequestConfig): AxiosPromise<CheckEmailResponse> {
            return localVarFp.checkClientEmail(checkEmailRequest, options).then((request) => request(axios, basePath));
        },
        /**
         * Called after Firebase Email Link sign-in completes successfully. This endpoint: 1. Validates the Firebase token 2. Finds the Client entity by email 3. Sets custom claims on the Firebase user (userType, organizationId, clientId) 4. Links the Firebase UID to the Client entity  The response includes the client and organization IDs needed by the portal.  **Important:** After calling this endpoint, the frontend must refresh the Firebase token to get the updated custom claims. 
         * @summary Complete client portal sign-in
         * @param {*} [options] Override http request option.
         * @throws {RequiredError}
         */
        completeClientSignIn(options?: RawAxiosRequestConfig): AxiosPromise<ClientAuthResponse> {
            return localVarFp.completeClientSignIn(options).then((request) => request(axios, basePath));
        },
    };
};

/**
 * ClientAuthApi - object-oriented interface
 */
export class ClientAuthApi extends BaseAPI {
    /**
     * Sends the client portal\'s magic sign-in link to the supplied address when a client record names it, and an informational email saying no record is registered on that address when none does. Exactly one email goes out either way, and the frontend sends nothing itself.  **Security note (ADR-013):** to prevent email enumeration this endpoint answers 200 OK with the same body in both cases. The difference reaches the mailbox only — and even there the two mails share a subject line. A Firebase sign-in link is minted *only* for an address a client record names, because redeeming one creates a Firebase Auth account, and an account with no `clients` row behind it is what locks a client out later (#2762/#2763/#2764).  The path and operation are still named `check-email`, which is what they did before the send moved server-side. Renaming the path would 404 every browser still running the previous frontend bundle for the whole of a rolling deploy, on the only route a client has into their dossier, so the name is historical and this description is what carries the meaning.  Rate limited per requested address and per source IP. The 429 is enumeration-safe: an unknown address is throttled exactly like a known one. 
     * @summary Request a client portal sign-in link
     * @param {CheckEmailRequest} checkEmailRequest 
     * @param {*} [options] Override http request option.
     * @throws {RequiredError}
     */
    public checkClientEmail(checkEmailRequest: CheckEmailRequest, options?: RawAxiosRequestConfig) {
        return ClientAuthApiFp(this.configuration).checkClientEmail(checkEmailRequest, options).then((request) => request(this.axios, this.basePath));
    }

    /**
     * Called after Firebase Email Link sign-in completes successfully. This endpoint: 1. Validates the Firebase token 2. Finds the Client entity by email 3. Sets custom claims on the Firebase user (userType, organizationId, clientId) 4. Links the Firebase UID to the Client entity  The response includes the client and organization IDs needed by the portal.  **Important:** After calling this endpoint, the frontend must refresh the Firebase token to get the updated custom claims. 
     * @summary Complete client portal sign-in
     * @param {*} [options] Override http request option.
     * @throws {RequiredError}
     */
    public completeClientSignIn(options?: RawAxiosRequestConfig) {
        return ClientAuthApiFp(this.configuration).completeClientSignIn(options).then((request) => request(this.axios, this.basePath));
    }
}



